import { HubConnectionState } from "@microsoft/signalr";
import { computed, ref } from "vue";



import { useBillStore } from "@/data/stores/bill";
import { useCartStore } from "@/data/stores/cart";
import { useCartPaymentStore } from "@/data/stores/cartPayment";
import { useOfferStore } from "@/data/stores/offer";
import { usePaymentStore } from "@/data/stores/payment";
import { useTableAllocationStore } from "@/data/stores/tableAllocation";



import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { AppSocket } from "@/data/dao/socket/AppSocket";
import useAppSocket from "@/data/dao/socket/composables/appSocket";
import { dataEvents } from "@/data/events/dataEvents";
import type { ISelectedItem } from "@/data/model/Bill";
import type { ICustomerArticle, ICustomerArticleWithOptions, IOrderPaymentConfirmation } from "@/data/model/Customer";
import { BillType, PaymentFor, UserItemSelectionChangeReason } from "@/data/model/Enums";
import type { BillItemSelectionChangedEvent, IAllocationuseCaseChangedEvent, IBillChangedEvent, IBillChangedSharedEvent, IBillClosedEvent, IBillDiscountChangedEvent, IBillDiscountChangedSharedEvent, IBillManualEndUsersCountChangedEvent, IBillNameChangedEvent, IBillOpenedEvent, ICustomerOfferNeedsRefreshEvent, IRoomOfferActivationChangedEvent, ITableCovidUserCountChangedEvent } from "@/data/model/Events";
import type { IService } from "@/data/model/Service";
import { shouldShowReceiptActionsForOperator } from "@/data/services/PaymentService";
import { exceptionTypes } from "@/data/util/Config";
import { Format } from "@/data/util/Format";



import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { DeviceFingerprintProvider } from "@/shared/services/DeviceFingerprintProvider";



import { Popup } from "@/components/dialog/Popup";
import ReceiptPopup from "@/components/payment/ReceiptPopup.vue";
import { ConnectionState } from "@/components/websocket/ConnectionState";



import { OperatorClient } from "@/operator/OperatorClient";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { trans_vue } from "@/operator/localization/TransVue";


export interface IPayPopupOptions {
    consumption: number;
    selectedItems: ISelectedItem[];
}

export function useOrderAndPayDataContext(
    locationId: string,
    tableId: string,
    billId: string | undefined,
    transactionId: string | undefined,
    externalPaymentId: string | undefined,
    transactionFor: PaymentFor | undefined) {
    // stores
    const bill = useBillStore();
    const cart = useCartStore();
    const cartPayment = useCartPaymentStore();
    const offer = useOfferStore();
    const tableAllocation = useTableAllocationStore();
    const payment = usePaymentStore();

    // local variables
    let appSocket!: AppSocket;
    let wasWebsocketConnectionLost: boolean = false;

    // refs
    const showPreorderTimeSelection = ref<boolean>(false);
    const isInitialized = ref(false);
    const isAllocationSuccess = ref(false);
    const selectedItem = ref(null as ICustomerArticleWithOptions | null);
    const isPayPopoverVisible = ref(false);
    const transactionIdInProgress = ref<string | undefined>(transactionId);
    const externalPaymentIdInProgress = ref<string | undefined>(externalPaymentId);
    const transactionForInProgress = ref<PaymentFor | undefined>(transactionFor);

    const payPopoverOptions = ref<IPayPopupOptions | null>(null);

    // flag meaning if selections has been reset while pay popover was open - if that's the case, then we should display a message to the user
    const shouldDisplayInvalidSelectionsPopup = ref(false);

    // computed actions
    const locationName = computed(() => {
        if (bill.currentBill != null && bill.currentBill.customName != null) {
            return `${tableAllocation.fullTableName} / ${bill.currentBill.customName}`;
        } else {
            return tableAllocation.fullTableName;
        }
    });
    const isAllocated = computed(() => isAllocationSuccess.value && tableAllocation.allocation != null);
    const isAnyPaymentService = computed(() => tableAllocation.paymentServices.length > 0);
    const isPayButtonActive = computed(
        () => isAnyPaymentService.value && bill.totalItemsSelected > 0 && !cartPayment.isProcessingPayment
    );

    async function initialize() {
        try {
            await allocateToTable();
            await loadCurrentCartIfNeeded();
            await setOrderServiceIfNeeded();
            await loadCurrentBill();
        } finally {
            isInitialized.value = true;
        }
    }

    async function initializeSocket() {
        const { appSocket: newAppSocket } = useAppSocket({ createNewInstance: true });
        appSocket = newAppSocket;
        appSocket.onConnectionChanged.add(async (state: HubConnectionState) => {
            ConnectionState.setBySignalR(state);

            const disconnectedStates = [
                HubConnectionState.Disconnected,
                HubConnectionState.Disconnecting,
                HubConnectionState.Reconnecting,
            ];

            if (disconnectedStates.includes(state)) {
                wasWebsocketConnectionLost = true;
                return;
            }

            if (isInitialized.value && wasWebsocketConnectionLost && state == HubConnectionState.Connected) {
                wasWebsocketConnectionLost = false;
                await initialize();
            }
        });
        await appSocket.connect();
    }

    async function disposeSocket() {
        appSocket?.dispose();
        appSocket?.disconnect();
    }

    async function allocateToTable() {
        try {
            // get browser fingerprint - required to map device to identity
            const deviceFingerprint = await DeviceFingerprintProvider.getFingerprint();

            await tableAllocation.allocateToTable(locationId, tableId, deviceFingerprint, billId);

            tableAllocation.initalize(
                OperatorClient.context.settings?.location ?? null,
                OperatorClient.context.settings?.planRoom ?? null
            );

            isAllocationSuccess.value = true;
        } catch (ex) {
            isAllocationSuccess.value = false;
            logError(ex);
            if (ex instanceof DaoError) {
                if (ex.type === exceptionTypes.unauthorizedException) {
                    showErrorAndSignOut(trans_vue.messages.unauthorizedPleaseRefresh);
                } else if (ex.type === exceptionTypes.deviceNotFoundException) {
                    showErrorAndSignOut(trans_vue.messages.deviceNotFoundPleaseRefresh);
                } else if (
                    ex.type === exceptionTypes.roomIsDeactivatedException ||
                    ex.type === exceptionTypes.tableIsDeactivatedException
                ) {
                    showTableNotActivePopup();
                    return;
                }
            }
        }

        if (isAllocationSuccess.value == false || tableAllocation.allocation == null) {
            showError(
                trans_vue.orderAndPayView.pleaseSelectAnotherTable,
                trans_vue.orderAndPayView.failedToAllocateToTable
            );
            return;
        }

        // select first order service
        const orderServiceToSet = tableAllocation.hasOrderServices ? tableAllocation.orderServices[0] : null;
        await setActiveOrderService(orderServiceToSet);

        // select first payment service
        if (tableAllocation.hasPaymentServices) {
            await payment.setPaymentService(tableAllocation.paymentServices[0]);
        } else {
            await payment.setPaymentService(null);
        }
    }

    async function refreshAllocation(options: { keepSelectedServices: boolean }) {
        const previousOrderServiceId = options?.keepSelectedServices ? offer.orderService?.id : undefined;
        const previousPaymentServiceId = options?.keepSelectedServices ? payment.paymentService?.id : undefined;

        try {
            await tableAllocation.refreshAllocationInfo();
        } catch (ex) {
            logError(ex);
        }

        if (tableAllocation.allocation == null) {
            logError("No allocation after refreshing table allocation info.");
            return;
        }

        await handleOrderServiceSelection(previousOrderServiceId);
        await handlePaymentServiceSelection(previousPaymentServiceId);
    }

    async function handleOrderServiceSelection(previousOrderServiceId: string | undefined) {
        // if previous order service was selected - select the same order service
        const previouslySelectedService = previousOrderServiceId ? getOrderServiceById(previousOrderServiceId) : null;
        if (previouslySelectedService) {
            await setActiveOrderService(previouslySelectedService);
            return;
        }

        // otherwise select first order service - if it's possible
        const orderServiceToSet = tableAllocation.hasOrderServices ? tableAllocation.orderServices[0] : null;
        await setActiveOrderService(orderServiceToSet);
    }

    async function handlePaymentServiceSelection(previousPaymentServiceId: string | null | undefined) {
        // if previous payment service was selected - select the same payment service
        const previouslySelectedService = previousPaymentServiceId
            ? getPaymentServiceById(previousPaymentServiceId)
            : null;
        if (previouslySelectedService) {
            await payment.setPaymentService(previouslySelectedService);
            return;
        }

        // otherwise select first payment service - if it's possible
        const paymentServiceToSet = tableAllocation.hasPaymentServices ? tableAllocation.paymentServices[0] : null;
        await payment.setPaymentService(paymentServiceToSet);
    }

    async function loadCurrentBill() {
        try {
            await bill.loadCurrentBill();
        } catch (ex) {
            logError(ex);
            showError(trans_vue.orderAndPayView.failedLoadingBill);
        }
    }

    async function setActiveOrderService(orderService: IService | null) {
        try {
            await offer.setOrderService(orderService);
        } catch (ex) {
            logError(ex);
            showError(trans_vue.orderAndPayView.failedLoadingOffer);
        }
    }

    function showErrorAndSignOut(errorMessage: string) {
        Popup.show({
            message: errorMessage,
            confirmHandler: async () => {
                try {
                    await OperatorClient.context.signOut();
                } finally {
                    window.location.href = "/";
                }
            },
        });
    }

    async function loadCurrentCartIfNeeded() {
        if (!offer.hasItemCategories && cart.currentCart == null) {
            await cart.loadCurrentCart();
        }
    }

    async function setOrderServiceIfNeeded() {
        if (cart.currentCart?.serviceId && cart.currentCart?.serviceId != offer.orderService?.id) {
            const serviceToSet = getOrderServiceById(cart.currentCart?.serviceId);
            await offer.setOrderService(serviceToSet);
        }
    }

    function getOrderServiceById(serviceId: string) {
        return tableAllocation.orderServices?.find((x) => x.id == serviceId) ?? null;
    }

    function getPaymentServiceById(serviceId: string) {
        return tableAllocation.paymentServices?.find((x) => x.id == serviceId) ?? null;
    }

    function showTableNotActivePopup() {
        Popup.show({
            title: trans_vue.orderAndPayView.failedToAllocateToTable,
            message: trans_vue.orderAndPayView.tableNotActive,
            uniqueId: "tableNotActivePopup",
            containerClass: "tableNotActivePopupContainer",
        });
    }

    function showError(message: string, title?: string) {
        Popup.show({
            title: title ?? trans.general.error,
            message: message,
        });
    }

    // define methods
    // order section methods
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function isItemAvailable(item: ICustomerArticle) {
        return true;
    }
    function selectItem(item: ICustomerArticleWithOptions) {
        selectedItem.value = item;
    }
    function unselectItem() {
        selectedItem.value = null;
    }

    // cart methods
    function payForBill() {
        payPopoverOptions.value = {
            consumption: bill.consumption,
            selectedItems: bill.selectedItemsList,
        };
        isPayPopoverVisible.value = true;
        shouldDisplayInvalidSelectionsPopup.value = false;
    }

    function closePaymentPopover() {
        isPayPopoverVisible.value = false;
        payPopoverOptions.value = null;

        if (shouldDisplayInvalidSelectionsPopup.value) {
            shouldDisplayInvalidSelectionsPopup.value = false;

            Popup.show({
                uniqueId: "pmtconfirm",
                message: translations.pay.failedBillInvalidSelection,
            });
        }
    }

    // navigation methods
    function goToRoomPlan() {
        operatorEvents.views.ShowRoomPlan.dispatch({
            showSettingsRoomPlan: false,
            locationId: tableAllocation.locationId,
            roomId: tableAllocation.roomId,
        });
    }

    async function onPaymentSuccess() {
        // todo: ts-1064 - this is a fallback - we should get websocket notification, but this should work just in case
        await bill.loadCurrentBill();
    }

    // entering the table selects all bill items to pay; not while the cart takes selection over
    // (see BillSummarySection), because the bill selection would be dropped right away, and not with
    // an own voucher applied, because the server rejects the change and prompts to remove the voucher
    async function selectAllBillItems() {
        // If a payment is in progress, we don't want all the items to be selected
        if (transactionIdInProgress.value != null || externalPaymentIdInProgress.value != null) {
            return;
        }

        const isCartSelectionActive = cart.hasItems && offer.areDiscountsEnabled;
        const hasOwnVoucherApplied = bill.currentBill?.appliedDiscounts.some(
            (x) => x.voucherId != null && x.identityId === tableAllocation.allocation?.identityId
        );
        // the server ignores an unchanged selection, but the store would still refetch the bill for its totals
        const isEverythingSelected = bill.unpaidItems.every((x) => x.selectedQuantity === x.unpaidQuantity);
        if (
            !tableAllocation.hasPaymentServices ||
            isCartSelectionActive ||
            hasOwnVoucherApplied ||
            isEverythingSelected
        ) {
            return;
        }

        try {
            await bill.selectAllItems();
        } catch (ex) {
            logError(ex);
        }
    }

    async function initView() {
        await initialize();
        await initializeSocket();

        dataEvents.appSocket.AllocationChange.add(handleAllocationChanged);
        dataEvents.appSocket.AllocationUseCaseChange.add(handleAllocationUseCaseChange);
        dataEvents.appSocket.BillOpened.add(handleBillOpened);
        dataEvents.appSocket.BillChanged.add(handleBillChanged);
        dataEvents.appSocket.BillChangedShared.add(handleBillChangedShared);
        dataEvents.appSocket.BillNameChanged.add(handleBillNameChanged);
        dataEvents.appSocket.BillManualEndUsersCountChanged.add(handleBillManualEndUsersCountChanged);
        dataEvents.appSocket.BillClosed.add(handleBillClosed);
        dataEvents.appSocket.CartChanged.add(handleCartChanged);
        dataEvents.appSocket.ItemSelectionChanged.add(handleItemSelectionChanged);
        dataEvents.appSocket.OfferChanged.add(handleOfferChanged);
        dataEvents.appSocket.PaymentConfirmed.add(handleOrderConfirmed);
        dataEvents.appSocket.RoomOfferActivationChanged.add(handleRoomOfferActivationChanged);
        dataEvents.appSocket.TableCovidUserCountChanged.add(handleGuestNumberChanged);
        dataEvents.appSocket.UserAllocated.add(handleUserAllocated);
        dataEvents.appSocket.UserDeallocated.add(handleUserDeallocated);
        dataEvents.appSocket.BillDiscountChanged.add(handleBillDiscountChangedEvent);
        dataEvents.appSocket.BillDiscountChangedShared.add(handleBillDiscountChangedSharedEvent);
        operatorEvents.payContext.invalidSelectionsPopupViewed.add(handleInvalidSelectionsPopupViewed);
        operatorEvents.context.signingOut.add(handleSigningOut);
        operatorEvents.payContext.cartTotalDiscountMismatchPopupViewed.add(handleCartTotalDiscountMismatchPopupViewed);

        await selectAllBillItems();
    }

    async function disposeView() {
        dataEvents.appSocket.AllocationChange.remove(handleAllocationChanged);
        dataEvents.appSocket.AllocationUseCaseChange.remove(handleAllocationUseCaseChange);
        dataEvents.appSocket.BillOpened.remove(handleBillOpened);
        dataEvents.appSocket.BillChanged.remove(handleBillChanged);
        dataEvents.appSocket.BillChangedShared.remove(handleBillChangedShared);
        dataEvents.appSocket.BillNameChanged.remove(handleBillNameChanged);
        dataEvents.appSocket.BillManualEndUsersCountChanged.remove(handleBillManualEndUsersCountChanged);
        dataEvents.appSocket.BillClosed.remove(handleBillClosed);
        dataEvents.appSocket.CartChanged.remove(handleCartChanged);
        dataEvents.appSocket.ItemSelectionChanged.remove(handleItemSelectionChanged);
        dataEvents.appSocket.OfferChanged.remove(handleOfferChanged);
        dataEvents.appSocket.PaymentConfirmed.remove(handleOrderConfirmed);
        dataEvents.appSocket.RoomOfferActivationChanged.remove(handleRoomOfferActivationChanged);
        dataEvents.appSocket.TableCovidUserCountChanged.remove(handleGuestNumberChanged);
        dataEvents.appSocket.UserAllocated.remove(handleUserAllocated);
        dataEvents.appSocket.UserDeallocated.remove(handleUserDeallocated);
        dataEvents.appSocket.BillDiscountChanged.remove(handleBillDiscountChangedEvent);
        dataEvents.appSocket.BillDiscountChangedShared.remove(handleBillDiscountChangedSharedEvent);
        operatorEvents.payContext.invalidSelectionsPopupViewed.remove(handleInvalidSelectionsPopupViewed);
        operatorEvents.context.signingOut.remove(handleSigningOut);
        operatorEvents.payContext.cartTotalDiscountMismatchPopupViewed.remove(
            handleCartTotalDiscountMismatchPopupViewed
        );
        await disposeSocket();
    }

    // event handlers
    async function handleAllocationChanged() {
        await tableAllocation.refreshAllocationInfo();
        await bill.loadCurrentBill();

        // select first order service
        let orderServiceToSet = tableAllocation.hasOrderServices ? tableAllocation.orderServices[0] : null;
        // select previous offer service
        if (
            offer.orderService &&
            tableAllocation.hasOrderServices &&
            tableAllocation.orderServices.some((x) => x.id == offer.orderService?.id)
        ) {
            orderServiceToSet = tableAllocation.orderServices.find((x) => x.id == offer.orderService?.id) ?? null;
        }
        await setActiveOrderService(orderServiceToSet);
        await setOrderServiceIfNeeded();

        // select first payment service
        if (tableAllocation.hasPaymentServices) {
            await payment.setPaymentService(tableAllocation.paymentServices[0]);
        } else {
            await payment.setPaymentService(null);
        }
    }

    async function handleAllocationUseCaseChange(ev: IAllocationuseCaseChangedEvent) {
        if (tableAllocation.isAllocatedToTable(ev.tableId)) {
            await refreshAllocation({ keepSelectedServices: true });
        }
    }

    async function handleBillOpened(ev: IBillOpenedEvent) {
        if (
            !isAllocated.value ||
            ev.tableId != tableAllocation.tableId ||
            ev.bill.type !== BillType.TableBill ||
            tableAllocation.allocation?.orderBillId != null ||
            ev.billId == bill.currentBill?.id
        )
            return;

        bill.setCurrentBill(ev.bill);
        await tableAllocation.refreshGuestsInfo();
    }

    async function handleBillChanged(ev: IBillChangedEvent) {
        if (!isAllocated.value || ev.billId != bill.currentBill?.id) return;

        bill.setCurrentBill(ev.bill);
        await tableAllocation.refreshGuestsInfo();
    }

    async function handleBillChangedShared(ev: IBillChangedSharedEvent) {
        const isIdentityToIgnore = ev.identitiesToIgnore.includes(tableAllocation.allocation?.identityId!);
        if (isIdentityToIgnore) return;

        handleBillChanged(ev);
    }

    async function handleBillNameChanged(ev: IBillNameChangedEvent) {
        if (!isAllocated.value || ev.billId != bill.currentBill?.id) return;

        bill.updateBillName(ev.billId, ev.name);
    }

    async function handleBillManualEndUsersCountChanged(ev: IBillManualEndUsersCountChangedEvent) {
        if (!isAllocated.value || ev.billId != bill.currentBill?.id) return;

        bill.updateBillManualEndUsersCount(ev.billId, ev.manualEndUsersCount);
    }

    async function handleBillClosed(ev: IBillClosedEvent) {
        if (!isAllocated.value || ev.billId != bill.currentBill?.id) return;

        bill.setCurrentBill(null);
        await tableAllocation.refreshGuestsInfo();
        await refreshAllocation({ keepSelectedServices: false });
    }

    async function handleBillDiscountChangedEvent(ev: IBillDiscountChangedEvent) {
        if (!isAllocated.value || ev.billId != bill.currentBill?.id) return;

        bill.setCurrentBill(ev.bill);

        payPopoverOptions.value = {
            consumption: bill.consumption,
            selectedItems: bill.selectedItemsList,
        };

        await tableAllocation.refreshGuestsInfo();
    }

    async function handleBillDiscountChangedSharedEvent(ev: IBillDiscountChangedSharedEvent) {
        const isIdentityToIgnore = ev.identitiesToIgnore.includes(tableAllocation.allocation?.identityId!);
        if (isIdentityToIgnore) return;

        handleBillDiscountChangedEvent(ev);
    }

    async function handleCartChanged(ev) {
        if (!isAllocated.value) return;

        if (!ev.isUserAction) {
            Popup.show({
                message: `
                <strong>${Format.htmlEscape(translations.pay.cartChangedTitle)}</strong>
                <br />
                <br />
                ${Format.htmlEscape(translations.pay.cartChangedMessage)}`,
                uniqueId: "totalChanged",
            });
        }

        cart.setCurrentCart(ev.cart ?? null);
    }

    async function handleGuestNumberChanged(ev: ITableCovidUserCountChangedEvent) {
        if (!isAllocated.value) return;

        if (tableAllocation.tableId != ev.tableId) return;

        await tableAllocation.refreshGuestsInfo();
    }

    function handleInvalidSelectionsPopupViewed() {
        shouldDisplayInvalidSelectionsPopup.value = false;
    }

    async function handleItemSelectionChanged(eventData: BillItemSelectionChangedEvent) {
        if (!isAllocated.value) return;

        if (
            eventData.billId !== bill.currentBill?.id ||
            eventData.identityId !== tableAllocation.allocation?.identityId
        ) {
            return;
        }

        bill.updateSelectedItems(eventData.selections);

        if (eventData.reason == UserItemSelectionChangeReason.SelectionsReset) {
            if (isPayPopoverVisible.value) {
                shouldDisplayInvalidSelectionsPopup.value = true;
            } else {
                Popup.show({
                    uniqueId: "pmtconfirm",
                    message: translations.pay.failedBillInvalidSelection,
                });
            }
        }
    }

    async function handleOfferChanged(ev: ICustomerOfferNeedsRefreshEvent) {
        if (!isAllocated.value || ev.roomId != tableAllocation.roomId) return;

        await refreshAllocation({ keepSelectedServices: true });
    }

    async function handleOrderConfirmed(eventData: IOrderPaymentConfirmation) {
        if (offer.isPreorderService) {
            showPreorderTimeSelection.value = true;
        }

        if (!isAllocated.value || eventData.isConfirmed === false) return;

        if (eventData.paymentFor == PaymentFor.Order) {
            // show order received popup only when Order use case is set to Order number ON
            if (eventData.dailyOrderIncrement === undefined) {
                displayReceiptPopup(eventData);
                return;
            }

            Popup.show({
                title: trans.orderReceivedPopup.received,
                message: `<div class="pay-later-order-received-order-number">${eventData.dailyOrderIncrement}</div>`,
                confirmHandler() {
                    displayReceiptPopup(eventData);
                },
            });
        } else {
            displayReceiptPopup(eventData);
        }
    }

    function displayReceiptPopup(confirmation: IOrderPaymentConfirmation) {
        const showReceiptPopup = shouldShowReceiptActionsForOperator({
            confirmation: confirmation,
            isWaiter: true,
            orderServices: tableAllocation.orderServices,
            paymentServices: tableAllocation.paymentServices,
        });

        if (!showReceiptPopup) {
            return;
        }

        const popup = Popup.show({
            component: ReceiptPopup,
            componentProps: {
                locationId: locationId,
                paymentFor: confirmation.paymentFor,
                orderId: confirmation.orderId,
                paymentId: confirmation.paymentId,
                isPayLater: false,
                closePopup: () => popup?.close(),
                userIsWaiter: true,
            },
            showConfirmButton: false,
            closePopupOnOverlayClick: true,
            showCloseButton: true,
        });
    }

    async function handleRoomOfferActivationChanged(ev: IRoomOfferActivationChangedEvent) {
        if (!isAllocated.value || ev.roomId != tableAllocation.roomId) return;

        await refreshAllocation({ keepSelectedServices: true });
    }

    async function handleUserAllocated() {}

    async function handleUserDeallocated() {
        operatorEvents.views.ShowRoomPlan.dispatch({
            showSettingsRoomPlan: false,
            locationId: tableAllocation.locationId,
            roomId: tableAllocation.roomId,
        });
    }

    function handleSigningOut() {
        disposeView();
    }

    async function handleCartTotalDiscountMismatchPopupViewed() {
        await cart.loadCurrentCart();
    }

    return {
        // refs
        isInitialized,
        selectedItem,
        isPayPopoverVisible,
        payPopoverOptions,
        showPreorderTimeSelection,
        externalPaymentIdInProgress,
        transactionIdInProgress,
        transactionForInProgress,

        // getters
        isAllocated,
        isPayButtonActive,
        locationName,

        // actions
        payForBill,
        closePaymentPopover,
        onPaymentSuccess,
        isItemAvailable,
        selectItem,
        unselectItem,
        goToRoomPlan,
        initView,
        disposeView,

        // for testing purposes
        tst_initialize: initialize,
        tst_handleBillOpened: handleBillOpened,
        tst_handleBillChanged: handleBillChanged,
        tst_handleBillNameChanged: handleBillNameChanged,
        tst_handleBillClosed: handleBillClosed,
    };
}
